/** EPN-239 — the time zone in which Employee Payslip EVENT times (approved, processing, paid, voided) are shown.
 *
 * Rules:
 *  - An event time is a UTC instant on the wire (`statusSince`, ISO-8601 with a trailing Z). It is formatted with `Intl.DateTimeFormat` in an IANA zone, so
 *    daylight saving is applied by the platform's own tz data (PST in winter, PDT in summer); no UTC offset is ever hard-coded here.
 *  - The DEFAULT zone is the viewer's own browser time zone. The viewer may pick another from a short list; an explicit choice is remembered in
 *    localStorage (try/catch: it works without storage, and a stored value that is not on the list is ignored). No stored choice means the browser zone.
 *  - If the browser's zone cannot be resolved, UTC is used and the indicator says so.
 *  - A PAY DATE is a plain calendar date (`YYYY-MM-DD`) and never goes through this module: it is never time-zone shifted (see `formatReportDate`). */

export const BROWSER_ZONE = 'browser';

/** The selectable zones after "My browser time zone". `label` is the friendly name used in the indicator. */
export const NAMED_ZONES = [
  { id: 'America/Los_Angeles', label: 'Pacific' },
  { id: 'America/Denver', label: 'Mountain' },
  { id: 'America/Chicago', label: 'Central' },
  { id: 'America/New_York', label: 'Eastern' },
  { id: 'America/Anchorage', label: 'Alaska' },
  { id: 'Pacific/Honolulu', label: 'Hawaii' },
  { id: 'UTC', label: 'UTC' },
] as const;

export const TIME_ZONE_STORAGE_KEY = 'epn-payslip-time-zone';

/** What the viewer picked: `browser` or the IANA id of one of {@link NAMED_ZONES}. */
export type TimeZoneChoice = typeof BROWSER_ZONE | (typeof NAMED_ZONES)[number]['id'];

function isValidZone(zone: string): boolean {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: zone });
    return true;
  } catch {
    return false;
  }
}

/** The viewer's browser zone (IANA id), or null when it cannot be determined. */
export function browserTimeZone(): string | null {
  try {
    const zone = new Intl.DateTimeFormat().resolvedOptions().timeZone;
    return zone && isValidZone(zone) ? zone : null;
  } catch {
    return null;
  }
}

function isChoice(value: string | null): value is TimeZoneChoice {
  return value !== null && (value === BROWSER_ZONE || NAMED_ZONES.some((zone) => zone.id === value));
}

/** The remembered choice, or `browser` when nothing valid is stored (or storage is unavailable). */
export function loadTimeZoneChoice(): TimeZoneChoice {
  try {
    const stored = window.localStorage.getItem(TIME_ZONE_STORAGE_KEY);
    return isChoice(stored) ? stored : BROWSER_ZONE;
  } catch {
    return BROWSER_ZONE;
  }
}

/** Remembers an explicit choice; choosing the browser zone forgets any stored one. Never throws. */
export function saveTimeZoneChoice(choice: TimeZoneChoice): void {
  try {
    if (choice === BROWSER_ZONE) window.localStorage.removeItem(TIME_ZONE_STORAGE_KEY);
    else window.localStorage.setItem(TIME_ZONE_STORAGE_KEY, choice);
  } catch {
    // Storage is blocked or full: the choice still applies for this page view.
  }
}

export interface ResolvedTimeZone {
  /** The IANA zone used to format. */
  zone: string;
  /** True when the browser's zone was wanted but could not be resolved, so UTC is used. */
  browserUnavailable: boolean;
}

export function resolveTimeZone(choice: TimeZoneChoice): ResolvedTimeZone {
  if (choice !== BROWSER_ZONE) return { zone: choice, browserUnavailable: false };
  const browser = browserTimeZone();
  return browser ? { zone: browser, browserUnavailable: false } : { zone: 'UTC', browserUnavailable: true };
}

/** `YYYY-MM-DD` with nothing after it: a calendar date, not an instant. */
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

const formatterCache = new Map<string, Intl.DateTimeFormat>();
function eventFormatter(zone: string): Intl.DateTimeFormat {
  let formatter = formatterCache.get(zone);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: zone, year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short',
    });
    formatterCache.set(zone, formatter);
  }
  return formatter;
}

/** "GMT+8" reads better as "UTC+8"; named abbreviations (PST, PDT, HST) are left alone. */
function utcStyle(name: string): string {
  return name.replace(/\bGMT(?=[+-]|$)/, 'UTC');
}

/** An event instant as "Feb 16, 2026, 3:42 PM PST" in `zone`. Returns null when the value is not a usable instant, so a caller shows no note rather than a
 * wrong one. A date-only value (no time of day) is shown as the plain calendar date and never shifted. */
export function formatEventInstant(value: string | null | undefined, zone: string): string | null {
  if (!value) return null;
  if (DATE_ONLY.test(value)) {
    const [year, month, day] = value.split('-').map(Number) as [number, number, number];
    return new Date(Date.UTC(year, month - 1, day)).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' });
  }
  const instant = new Date(value);
  if (Number.isNaN(instant.getTime())) return null;
  return utcStyle(eventFormatter(zone).format(instant));
}

function zoneNamePart(zone: string, style: 'short' | 'shortGeneric', at: Date): string | undefined {
  try {
    return new Intl.DateTimeFormat('en-US', { timeZone: zone, timeZoneName: style }).formatToParts(at).find((p) => p.type === 'timeZoneName')?.value;
  } catch {
    return undefined;
  }
}

/** The short label of a zone for the indicator. The generic abbreviation (PT, ET, HST) is preferred because it does not change with daylight saving, so it
 * agrees with both the "PST" and the "PDT" of the event notes; zones with no such abbreviation get their offset ("UTC+8", "UTC+5:30"). */
function zoneLabel(zone: string, at: Date): string {
  const generic = zoneNamePart(zone, 'shortGeneric', at);
  if (generic && /^[A-Z]{2,4}$/.test(generic) && generic !== 'GMT') return generic;
  return utcStyle(zoneNamePart(zone, 'short', at) ?? zone);
}

/** The text of the visible indicator, e.g. "Times shown in Pacific Time (PT)" or "Times shown in Asia/Manila (UTC+8)". `at` is the moment an offset label
 * is taken for (only zones without a generic abbreviation have one, and it can change with daylight saving). */
export function timeZoneIndicatorText(choice: TimeZoneChoice, at: Date = new Date()): string {
  const { zone, browserUnavailable } = resolveTimeZone(choice);
  if (browserUnavailable) return 'Times shown in UTC (your browser time zone could not be determined)';
  const named = choice === BROWSER_ZONE ? undefined : NAMED_ZONES.find((z) => z.id === choice);
  if (named && named.id === 'UTC') return 'Times shown in UTC';
  if (named) return `Times shown in ${named.label} Time (${zoneLabel(zone, at)})`;
  return `Times shown in ${zone} (${zoneLabel(zone, at)})`;
}

/** The selector's options, in order: the browser zone first (the default), then the named zones. */
export function timeZoneOptions(): { value: TimeZoneChoice; label: string }[] {
  const browser = browserTimeZone();
  return [
    { value: BROWSER_ZONE, label: browser ? `My browser time zone (${browser})` : 'My browser time zone (unavailable, using UTC)' },
    ...NAMED_ZONES.map((zone) => ({ value: zone.id as TimeZoneChoice, label: zone.id === 'UTC' ? 'UTC' : `${zone.label} Time` })),
  ];
}
